/*
 * WHAT COUNTS AS THE MODEL DOING SOMETHING — the one home of that rule.
 *
 * The engine reports a transition for every event, even one that lands the
 * model exactly where it was; a week of a busy signal is hundreds of thousands
 * of those. Everything that asks "did it move?" — the search, the worker's scan
 * mode, the replay's counts, the viewer's timeline and story, the AI tool's
 * outcome — answers it here, so a person and the assistant are never told two
 * different numbers for the same run.
 *
 * Pure and free of any DOM or worker import, so the worker, the lazily-loaded
 * search and the panel's entry chunk can all share it.
 */
import type { TraceEntry } from './protocol';

export type Transition = Extract<TraceEntry, { kind: 'transition' }>;

/** A configuration as one comparable value. */
export function configKey(to: readonly string[]): string {
  return to.join('+');
}

/**
 * The states a move newly ENTERED: those in the configuration it moved to that
 * were not in the one it left. A configuration lists leaves and their
 * ancestors, so a move between two children of `Overloaded` does not enter
 * `Overloaded` — the model was already in it. The search matches a chosen
 * state by this rule and the replay timeline highlights by it, so what lights
 * up is exactly what the search found.
 */
export function enteredStates(prev: readonly string[], to: readonly string[]): string[] {
  const before = new Set(prev);
  return to.filter((s) => !before.has(s));
}

/**
 * The transitions that actually MOVED the model: its boot, then every one whose
 * configuration differs from the one before. A model that stayed in Normal for
 * 1,767 readings reports 1,768 transitions, every one Normal → Normal; this is
 * the count a person means when they ask "did it change state?".
 */
export function stateChanges(trace: readonly TraceEntry[]): Transition[] {
  const out: Transition[] = [];
  let prev: string | null = null;
  for (const e of trace) {
    if (e.kind !== 'transition') continue;
    const key = configKey(e.to);
    if (key !== prev) out.push(e);
    prev = key;
  }
  return out;
}

/** How many times each state was entered across `moves`, starting from
 *  `before` — in order of first entry. */
export function entryCounts(moves: readonly Transition[], before: readonly string[] = []): Map<string, number> {
  const counts = new Map<string, number>();
  let prev = before;
  for (const m of moves) {
    for (const s of enteredStates(prev, m.to)) counts.set(s, (counts.get(s) ?? 0) + 1);
    prev = m.to;
  }
  return counts;
}

/**
 * A predicate keeping only activity. `initial` is the configuration before the
 * first entry, so a first step that lands back on it is not a change. Each
 * kept transition becomes the configuration the next is measured against.
 */
export function activityOnly(initial: readonly string[]): (e: TraceEntry) => boolean {
  let last = configKey(initial);
  return (e) => {
    if (e.kind === 'transition') {
      const key = configKey(e.to);
      if (key === last) return false;
      last = key;
      return true;
    }
    return e.kind === 'write' || e.kind === 'error';
  };
}

// ── what a person asked the search for ─────────────────────────────────────

/**
 * WHAT TO KEEP. A period is kept when it satisfies ANY of the conditions named
 * — a person who chooses two states wants either, and each extra choice widens
 * the list rather than emptying it. Plain data: the panel shows it back as a
 * sentence, and it can cross a tool boundary. Absent or empty, every episode is
 * kept.
 */
export interface ScanFilter {
  /** Kept if it entered any of these — state ids exactly as the document spells them. */
  states?: readonly string[];
  /** Kept if it would have written to the machine. */
  writes?: boolean;
  /** Kept if it hit a script error. */
  errors?: boolean;
}

export function isEmptyFilter(f: ScanFilter | null | undefined): boolean {
  return !f || (!f.states?.length && !f.writes && !f.errors);
}
